import React, { useEffect, useRef, useState } from "react";
import { MapPin, CaretDown, MagnifyingGlass } from "@phosphor-icons/react";
import { hasPlacesKey, loadPlacesLibrary } from "@/lib/googleMapsLoader";

/**
 * Curated Aruba location suggestions for finite categories.
 * (Restaurants/Dining and generic vacation-rental subareas are intentionally
 * NOT curated — those are handled by live Google Places Autocomplete.)
 */
export const ARUBA_LOCATIONS = [
  { group: "Airport & ports", items: [
    "Aruba Intl. Airport (AUA)",
    "Oranjestad Cruise Port",
  ]},
  { group: "Palm Beach hotels", items: [
    "Divi Aruba Phoenix Beach Resort",
    "Aruba Marriott Resort",
    "Hyatt Regency Aruba",
    "The Ritz-Carlton, Aruba",
    "Hilton Aruba Caribbean Resort",
    "Riu Palace Aruba",
    "Barceló Aruba",
    "Holiday Inn Resort Aruba",
  ]},
  { group: "Eagle Beach hotels", items: [
    "Bucuti & Tara Beach Resort",
    "Manchebo Beach Resort",
    "Amsterdam Manor Beach Resort",
    "Divi Aruba All Inclusive",
    "Tamarijn Aruba All Inclusive",
  ]},
  { group: "Vacation rentals · villas · Airbnbs", items: [
    "Villa / Airbnb / vacation rental — type your address",
  ]},
  { group: "Neighborhoods & destinations", items: [
    "Oranjestad Downtown",
    "San Nicolas Art District",
    "Arikok National Park",
    "Arashi Beach",
    "California Lighthouse",
    "Baby Beach",
    "Mangel Halto",
  ]},
];

const VILLA_CUE_LABEL = "Villa / Airbnb / vacation rental — type your address";

function flatten(list) {
  return list.flatMap((g) => g.items.map((i) => ({ label: i, group: g.group })));
}

/**
 * Hybrid location combobox:
 *  - Curated finite groups (hotels, airport, landmarks) render immediately.
 *  - As the user types, Google Places predictions restricted to Aruba
 *    (includedRegionCodes: ['aw']) appear in a "Search Aruba (live)" section.
 *  - If the Places key is missing, degrades cleanly to curated + free-text.
 *  - Free-text is always accepted on submit — nothing forces a selection.
 */
export default function LocationCombobox({
  value,
  onChange,
  placeholder,
  variant = "dark",
  testId,
  suggestions = ARUBA_LOCATIONS,
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [placesReady, setPlacesReady] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [placesLoading, setPlacesLoading] = useState(false);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const tokenRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const isDark = variant === "dark";
  const placesEnabled = hasPlacesKey();

  const flat = flatten(suggestions);
  const q = (value || "").toLowerCase().trim();
  const filtered = q
    ? flat.filter((it) => it.label.toLowerCase().includes(q) || it.group.toLowerCase().includes(q))
    : flat;

  // Lazy-load Google Places on first focus
  const ensurePlaces = async () => {
    if (!placesEnabled) return null;
    try {
      const lib = await loadPlacesLibrary();
      if (!tokenRef.current && lib.AutocompleteSessionToken) {
        tokenRef.current = new lib.AutocompleteSessionToken();
      }
      setPlacesReady(true);
      return lib;
    } catch (e) {
      // Silent fallback — curated + free-text still works
      return null;
    }
  };

  const runPlacesSearch = async (text) => {
    if (!placesEnabled || !text || text.trim().length < 2) {
      setPredictions([]);
      return;
    }
    const lib = await ensurePlaces();
    if (!lib || !lib.AutocompleteSuggestion) return;
    const reqId = ++requestIdRef.current;
    setPlacesLoading(true);
    try {
      const { suggestions: sug } = await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: text,
        includedRegionCodes: ["aw"],
        sessionToken: tokenRef.current,
      });
      if (reqId !== requestIdRef.current) return;
      const preds = (sug || [])
        .map((s) => s.placePrediction)
        .filter(Boolean)
        .slice(0, 6);
      setPredictions(preds);
    } catch (e) {
      setPredictions([]);
    } finally {
      if (reqId === requestIdRef.current) setPlacesLoading(false);
    }
  };

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const inputCls = isDark
    ? "w-full bg-transparent border-b border-offwhite/25 focus:border-gold-bright focus:outline-none py-2.5 pl-6 pr-6 text-offwhite placeholder:text-offwhite/40 text-sm"
    : "w-full bg-transparent border-b border-navy/20 focus:border-gold focus:outline-none py-2.5 pl-6 pr-6 text-navy placeholder:text-navy/40 text-sm";
  const popCls = isDark
    ? "absolute left-0 right-0 top-full z-30 mt-1 max-h-72 overflow-auto border border-gold-bright/40 bg-navy-deep shadow-xl"
    : "absolute left-0 right-0 top-full z-30 mt-1 max-h-72 overflow-auto border border-cream2 bg-cream shadow-xl";
  const groupHdrCls = isDark
    ? "sticky top-0 px-3 py-1.5 text-[9px] uppercase tracking-[0.22em] bg-navy-deep/95 text-teal-bright border-b border-navy-soft"
    : "sticky top-0 px-3 py-1.5 text-[9px] uppercase tracking-[0.22em] bg-cream/95 text-teal border-b border-cream2";
  const itemCls = (active) => isDark
    ? `w-full text-left px-3 py-2 text-sm block transition-colors ${active ? "bg-navy-soft text-offwhite" : "text-offwhite/85 hover:bg-navy-soft"}`
    : `w-full text-left px-3 py-2 text-sm block transition-colors ${active ? "bg-cream2/70 text-navy" : "text-navy/85 hover:bg-cream2/50"}`;

  const pickCurated = (item) => {
    // Villa cue: clear input + refocus so Places kicks in
    if (item.label === VILLA_CUE_LABEL) {
      onChange("");
      setPredictions([]);
      setOpen(true);
      setTimeout(() => inputRef.current?.focus(), 0);
      return;
    }
    onChange(item.label);
    setOpen(false);
    setHighlight(-1);
  };

  const pickPrediction = async (prediction) => {
    try {
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ["formattedAddress"] });
      onChange(place.formattedAddress || prediction.text.toString());
    } catch (e) {
      onChange(prediction.text.toString());
    }
    setOpen(false);
    setHighlight(-1);
    setPredictions([]);
    tokenRef.current = null; // fresh session on next lookup
  };

  const onKey = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) setOpen(true);
    if (e.key === "Escape") setOpen(false);
    // (Simple keyboard nav only across curated items to avoid over-engineering)
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, -1)); }
    else if (e.key === "Enter") {
      if (open && highlight >= 0 && filtered[highlight]) { e.preventDefault(); pickCurated(filtered[highlight]); }
    }
  };

  // Group filtered curated results while preserving order
  const grouped = filtered.reduce((acc, item, idx) => {
    const last = acc[acc.length - 1];
    if (last && last.group === item.group) last.items.push({ ...item, idx });
    else acc.push({ group: item.group, items: [{ ...item, idx }] });
    return acc;
  }, []);

  const hasPredictions = predictions.length > 0;
  const hasCurated = grouped.length > 0;
  const showEmpty = q.length > 0 && !hasCurated && !hasPredictions && !placesLoading;

  return (
    <div ref={rootRef} className="relative" data-testid={testId}>
      <MapPin
        size={14}
        weight="thin"
        className={`pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 ${isDark ? "text-teal-bright" : "text-teal"}`}
      />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v);
          setOpen(true);
          setHighlight(-1);
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => runPlacesSearch(v), 300);
        }}
        onFocus={() => { setOpen(true); ensurePlaces(); }}
        onKeyDown={onKey}
        placeholder={placeholder}
        className={inputCls}
        autoComplete="off"
        data-testid={testId ? `${testId}-input` : undefined}
        aria-autocomplete="list"
        aria-expanded={open}
      />
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        tabIndex={-1}
        className={`absolute right-0 top-1/2 -translate-y-1/2 p-1 ${isDark ? "text-teal-bright" : "text-teal"}`}
        aria-label="Toggle suggestions"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <CaretDown size={12} weight="thin" />
      </button>

      {open && (hasCurated || hasPredictions || placesLoading || showEmpty) && (
        <div className={popCls} role="listbox" data-testid={testId ? `${testId}-list` : undefined}>
          {(hasPredictions || placesLoading) && (
            <div data-testid={testId ? `${testId}-places-section` : undefined}>
              <div className={groupHdrCls}>
                <span className="inline-flex items-center gap-1.5">
                  <MagnifyingGlass size={10} weight="thin" />
                  Search Aruba (live)
                </span>
              </div>
              {placesLoading && !hasPredictions && (
                <div className={`px-3 py-2 text-xs ${isDark ? "text-offwhite/60" : "text-navy/60"}`}>
                  Searching Aruba…
                </div>
              )}
              {predictions.map((p) => (
                <button
                  key={p.placeId || p.text.toString()}
                  type="button"
                  onClick={() => pickPrediction(p)}
                  className={itemCls(false)}
                  data-testid={testId ? `${testId}-place-${(p.placeId || "p").slice(0, 24)}` : undefined}
                >
                  {p.text.toString()}
                </button>
              ))}
            </div>
          )}

          {grouped.map((g) => (
            <div key={g.group}>
              <div className={groupHdrCls}>{g.group}</div>
              {g.items.map((it) => (
                <button
                  key={`${g.group}-${it.label}`}
                  type="button"
                  onMouseEnter={() => setHighlight(it.idx)}
                  onClick={() => pickCurated(it)}
                  className={itemCls(it.idx === highlight)}
                  data-testid={`${testId}-opt-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 32)}`}
                >
                  {it.label}
                </button>
              ))}
            </div>
          ))}

          {showEmpty && (
            <div className={`px-3 py-3 text-xs ${isDark ? "text-offwhite/70" : "text-navy/70"}`}>
              {placesEnabled
                ? "No match yet — keep typing, we'll find it in Aruba."
                : "No match — just keep typing your address. We'll accept whatever you enter."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
